// src/data/disciplineLadder.js
//
// Record 611 (F20; Bob 10/8) - THE DISCIPLINE LADDER, AS ONE DEFINITION. Finding F20 (outputs\audit\
// AUDIT_FUNCTIONS_DATA_SCREENS.md): a case's stage, its number, its decision and its forward were written by the
// browser - advanceStage() and the Forward button wrote status and caseNumber directly, the browser advanced the
// committee's case-number counter, nothing recorded a decision after the case was opened, and nothing could resolve a
// forwarded case or reopen a resolved one. Bob's decisions of 2026-10-08:
//   (a) "Record decision" is added, and a resolved case may be reopened;
//   (b) a forwarded case reaches "resolved" through a General Chairperson step;
//   (c) a Local Chairperson resolves only a case that was NEVER forwarded; once forwarded, only the General
//       Chairperson or the administrator;
//   (d) a new case starts as a DRAFT and the SERVER numbers it (the browser counter is closed).
//
// Pure: no imports, no clock, no Firestore. The browser (src\components\DisciplineLadderPanel.js, the Forward button
// and the Open button in src\pages\DisciplineCases.js) uses it to OFFER an act; the disciplineAct Cloud Function
// (functions\index.js) uses the SAME code - projected as functions\shared\disciplineLadder.js by
// scripts\sync-shared-modules.js - to DECIDE it, in one transaction with the counter. firestore.rules refuses every
// client write to LADDER_FIELDS (disciplineLadderFields() there must equal the list below; test-discipline-ladder.mjs
// holds them together). Proved by scripts\test-discipline-ladder.mjs.

export const DRAFT = 'draft';
export const GC_REVIEW = 'gc_review';
export const DECISION_PENDING = 'decision_pending';
export const RESOLVED = 'resolved';

// The ladder, in order. gc_review is OFF the ladder: a case reaches it only by the forward.
export const LADDER = ['draft', 'notice_received', 'investigation_ordered', 'investigation_complete', 'appeal_filed',
  'decision_pending', 'resolved'];

export const STAGE_LABELS = {
  draft: 'Draft',
  notice_received: 'Notice Received',
  investigation_ordered: 'Investigation Ordered',
  investigation_complete: 'Investigation Complete',
  appeal_filed: 'Appeal Filed',
  decision_pending: 'Decision Pending',
  gc_review: 'GC Review',
  resolved: 'Resolved',
};

export const ACT_KINDS = ['advance', 'forward', 'decide', 'resolve', 'reopen'];

// The stages at which a decision may be recorded: once the investigation is complete, and before the case is resolved.
export const DECIDABLE = ['investigation_complete', 'appeal_filed', 'decision_pending', 'gc_review'];

// The decision vocabulary: DECISION_TYPES in src\pages\DisciplineCases.js as it stood on 2026-10-08, without
// 'Pending' (a pending decision is not a decision; it is the absence of one).
export const DECISION_OUTCOMES = [
  'Written Reprimand',
  'Demerit',
  'Suspension',
  'Dismissal',
  'Time / Discipline Held in Abeyance',
  'Held in Abeyance',
  'Charges Dismissed',
  'Discipline Reduced',
  'Exonerated',
  'Dropped Charges',
];

// The fields ONLY the disciplineAct function writes. firestore.rules disciplineLadderFields() is this list exactly.
// decision / decisionDate keep the names and the string shape the case has always carried (the Overview reads them;
// disciplineAuditHistory watches 'decision'), so no stored case changes meaning.
export const LADDER_FIELDS = ['status', 'caseNumber', 'decision', 'decisionDate', 'decisionNotes', 'decisionLetterPath',
  'decidedAt', 'decidedBy', 'decidedByName', 'forwardedToGCAt', 'forwardedToGCBy', 'forwardedToGCByUid',
  'resolvedAt', 'resolvedBy', 'resolvedByName', 'reopenedAt', 'reopenedBy', 'reopenedByName', 'reopenReason',
  'decisionTerms'];   // T5 / D-15: the decision's structured terms

export const NOTES_MAX = 4000;
export const REASON_MAX = 2000;

// The next rung on the ladder, or null at the top / off the ladder.
export function nextStage(status) {
  const i = LADDER.indexOf(String(status || ''));
  if (i < 0 || i >= LADDER.length - 1) return null;
  return LADDER[i + 1];
}

// The caller as the ladder reads him: the token's role, committee and scopes (custom claims, written only by the Admin
// SDK). The browser passes the page's user object, which App.js builds from the same token.
export function ladderActor(a) {
  const x = a || {};
  return {
    uid: String(x.uid || ''),
    role: x.isAdmin === true ? 'admin' : String(x.role || ''),
    committee: String(x.committee || ''),
    scopes: Array.isArray(x.scopes) ? x.scopes.map(String) : [],
    name: String(x.name || ''),
    email: String(x.email || ''),
  };
}

function isAdminOf(a) { return a.role === 'admin'; }
function isGcOf(a, k) {
  return a.role === 'general_chair' && a.committee !== '' && a.committee === String(k.generalCommittee || '');
}
function isLcOf(a, k) {
  const s = String(k.scopeKey || '');
  return a.role === 'local_chair' && s !== '' && a.scopes.indexOf(s) !== -1;
}

// A case was forwarded once it carries the forward stamp - and it stays "forwarded" after a reopening, so decision (c)
// holds for the life of the case.
export function wasForwarded(kase) {
  const k = kase || {};
  return !!(k.forwardedToGCAt || String(k.forwardedToGCBy || '') !== '' || String(k.status || '') === GC_REVIEW);
}

export function hasDecision(kase) {
  const d = String((kase && kase.decision) || '').trim();
  return d !== '' && d !== 'Pending';
}

// ---- Record 613 (Bob 10/8) - F22, THE TESTERS' TEST TERRITORY ----------------------------------------------------
// Bob 2026-10-08: "Agreed, help me set that up". A tester (role 'tester') whose token holds the scope TEST_SCOPE
// (scripts\set-roles.js writes it from data\YCP_USER_ROLES.csv) works an EXAMPLE case - marked isExample === true and
// filed in that territory, of his own committee - as its Local Chairperson would, and no other case (firestore.rules
// testerSandbox() is the same test for the client writes). An example is written by the browser as a DRAFT like every
// case; disciplineAct opens it and numbers it exampleCaseNumberFor() - never from counters/discipline_<committee>_<year>,
// so no real number is spent on it and none can be mistaken for one.
export const TEST_SCOPE = 'TEST|GO-342|SANDBOX';
export const EXAMPLE_PREFIX = 'EXAMPLE-';

// An example in the test territory: the EXAMPLE ONLY mark AND the territory (the rules bind the two together).
export function isSandboxExample(kase) {
  const k = kase || {};
  return k.isExample === true && String(k.scopeKey || '') === TEST_SCOPE;
}

// Does this caller work this case as its Local Chairperson would, through the test territory?
export function testerOnExample(actorIn, kase) {
  const a = ladderActor(actorIn);
  const k = kase || {};
  return a.role === 'tester' && isSandboxExample(k) && a.scopes.indexOf(TEST_SCOPE) !== -1
    && a.committee !== '' && a.committee === String(k.generalCommittee || '') && TEST_SCOPE === 'TEST|' + a.committee + '|SANDBOX';
}

// An example's number: EXAMPLE-<committee without the hyphen after GO>-<the case's own id>. Unique (the id is), stable,
// and never a DISC- sequence number (seqOf() reads it as 0, so the counter's first-use initialisation ignores it).
// A5 holds here too: no committee (or no id), no number.
export function exampleCaseNumberFor(committee, caseId) {
  const cmte = String(committee || '').trim();
  const id = String(caseId || '').trim();
  if (!cmte || !id) throw new Error('An example case is numbered from its committee and its own id, and one of them is missing.');
  return EXAMPLE_PREFIX + cmte.replace('GO-', 'GO') + '-' + id;
}

// WHO MAY DO WHAT. Returns { ok, to, because, why } - `to` is the status the act leaves the case at; `why` is 'who'
// (the caller may not) or 'state' (the case is not at a stage where the act exists), for the function's error code.
export function may(actorIn, kind, kase) {
  const a = ladderActor(actorIn);
  const k = kase || {};
  const st = String(k.status || '');
  const no = (why, because) => ({ ok: false, to: null, because: because, why: why });
  const yes = (to) => ({ ok: true, to: to, because: '', why: '' });
  if (!a.uid) return no('who', 'Sign in first.');
  if (ACT_KINDS.indexOf(kind) === -1) return no('state', 'Not an act on a discipline case: ' + kind + '.');
  if (!Object.prototype.hasOwnProperty.call(STAGE_LABELS, st)) {
    return no('state', 'This case carries a status the ladder does not know (' + (st || 'none') + '). The administrator must look at it.');
  }
  const admin = isAdminOf(a);
  const gc = isGcOf(a, k);
  const lc = isLcOf(a, k) || testerOnExample(a, k);   // Record 613 (Bob 10/8), F22: a tester on an EXAMPLE acts as its LC
  const fwd = wasForwarded(k);

  if (kind === 'advance') {
    if (st === DRAFT) {
      if (admin || a.uid === String(k.createdBy || '')) return yes(nextStage(DRAFT));
      return no('who', 'A draft is opened only by the person who wrote it, or the administrator.');
    }
    if (st === GC_REVIEW) return no('state', 'This case is with the General Chairperson. It does not move up the ladder from here: he records the decision and resolves it.');
    if (st === RESOLVED) return no('state', 'The case is resolved. The General Chairperson or the administrator may reopen it, with a reason.');
    if (st === DECISION_PENDING) return no('state', 'The next step is the decision: record it, then resolve the case.');
    if (!(admin || gc || lc)) return no('who', 'Only the Local Chairperson of the case\'s territory, the General Chairperson of its committee, or the administrator moves a case up the ladder.');
    return yes(nextStage(st));
  }

  if (kind === 'forward') {
    if (st === DRAFT) return no('state', 'A draft is opened before it is forwarded.');
    if (st === GC_REVIEW) return no('state', 'This case is already with the General Chairperson.');
    if (st === RESOLVED) return no('state', 'A resolved case is not forwarded. The General Chairperson may reopen it.');
    if (!(admin || lc)) return no('who', 'The Local Chairperson of the case\'s territory forwards it to the General Chairperson (or the administrator does).');
    return yes(GC_REVIEW);
  }

  if (kind === 'decide') {
    if (DECIDABLE.indexOf(st) === -1) return no('state', 'A decision is recorded once the investigation is complete, and before the case is resolved.');
    if (fwd) {
      if (admin || gc) return yes(st);
      return no('who', 'This case was forwarded. Only the General Chairperson of its committee or the administrator records its decision.');
    }
    if (admin || gc || lc) return yes(st);
    return no('who', 'Only the Local Chairperson of the case\'s territory, the General Chairperson of its committee, or the administrator records a decision.');
  }

  if (kind === 'resolve') {
    if (st !== DECISION_PENDING && st !== GC_REVIEW) {
      return no('state', 'A case is resolved from Decision Pending - or, once forwarded, from GC Review.');
    }
    if (fwd) {
      if (!(admin || gc)) return no('who', 'This case was forwarded. Only the General Chairperson of its committee or the administrator resolves it (decision (c), Bob 2026-10-08).');
    } else if (!(admin || gc || lc)) {
      return no('who', 'Only the Local Chairperson of the case\'s territory, the General Chairperson of its committee, or the administrator resolves a case.');
    }
    if (!hasDecision(k)) return no('state', 'Record the decision first - a resolved case carries its outcome.');
    return yes(RESOLVED);
  }

  // reopen
  if (st !== RESOLVED) return no('state', 'Only a resolved case is reopened.');
  if (!(admin || gc)) return no('who', 'Only the General Chairperson of the case\'s committee or the administrator reopens a resolved case.');
  return yes(fwd ? GC_REVIEW : DECISION_PENDING);
}

function validDate(s) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(s + 'T12:00:00Z');
  return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

// The decision as recorded: { outcome, decisionDate (yyyy-mm-dd), notes, letterPath }. letterPath is optional; when
// given it must be a file under this case's own storage folder (the upload pattern of the case screen).
// T5 / D-15 (2026-10-15): THE DECISION'S TERMS, structured - what was assessed, not only its name. Each term is a whole
// number or a date, or null when not recorded (never zero by default). suspensionDays on a Suspension, demerits on a
// Demerit, abeyanceMonths on a hold in abeyance; effectiveDate on any. A term that contradicts the outcome is refused.
export const TERM_LIMITS = { suspensionDays: 3650, demerits: 1000, abeyanceMonths: 120 };
function termsOf(raw, outcome, problems) {
  const t = raw || {};
  const out = { suspensionDays: null, demerits: null, abeyanceMonths: null, effectiveDate: null };
  Object.keys(TERM_LIMITS).forEach(function (f) {
    const v = t[f];
    if (v === undefined || v === null || String(v).trim() === '') return;
    const n = Number(String(v).trim());
    if (!Number.isInteger(n) || n < 1 || n > TERM_LIMITS[f]) { problems.push('The ' + f + ' term must be a whole number from 1 to ' + TERM_LIMITS[f] + ', or blank.'); return; }
    out[f] = n;
  });
  const ed = String(t.effectiveDate || '').trim();
  if (ed) { if (validDate(ed)) out.effectiveDate = ed; else problems.push('The effective date must be a real date (yyyy-mm-dd), or blank.'); }
  if (out.suspensionDays !== null && outcome !== 'Suspension' && outcome !== 'Discipline Reduced') problems.push('Suspension days belong to a Suspension (or a reduced discipline).');
  if (out.demerits !== null && outcome !== 'Demerit' && outcome !== 'Discipline Reduced') problems.push('Demerits belong to a Demerit outcome (or a reduced discipline).');
  if (out.abeyanceMonths !== null && !/Abeyance/.test(outcome)) problems.push('Months in abeyance belong to an outcome held in abeyance.');
  return out;
}

export function validateDecision(input, caseId) {
  const x = input || {};
  const problems = [];
  const outcome = String(x.outcome || '').trim();
  const decisionDate = String(x.decisionDate || '').trim();
  const notes = String(x.notes || '').trim();
  const letterPath = String(x.letterPath || '').trim();
  if (DECISION_OUTCOMES.indexOf(outcome) === -1) problems.push('Choose the outcome from the list.');
  if (!validDate(decisionDate)) problems.push('The decision date must be a real date (yyyy-mm-dd).');
  if (notes.length > NOTES_MAX) problems.push('The notes are longer than ' + NOTES_MAX + ' characters.');
  if (letterPath) {
    const folder = 'discipline/' + String(caseId || '') + '/';
    if (!caseId || letterPath.indexOf(folder) !== 0 || letterPath.indexOf('..') !== -1 || letterPath.length <= folder.length) {
      problems.push('The decision letter must be a file stored on this case.');
    }
  }
  const terms = termsOf(x.terms, outcome, problems);   // T5 / D-15
  return { ok: problems.length === 0, problems: problems, value: { outcome: outcome, decisionDate: decisionDate, notes: notes, letterPath: letterPath, terms: terms } };
}

export function validateReason(reason) {
  const r = String(reason || '').trim();
  if (!r) return { ok: false, problem: 'A reopening needs its reason.', value: '' };
  if (r.length > REASON_MAX) return { ok: false, problem: 'The reason is longer than ' + REASON_MAX + ' characters.', value: '' };
  return { ok: true, problem: '', value: r };
}

// ---- THE CASE NUMBER -------------------------------------------------------------------------------------------
// The format is generateCaseNumber()'s, exactly (DisciplineCases.js until Record 611): DISC-<committee without the
// hyphen after GO>-<year>-<sequence, three digits at least>. A5 (audit 2026-09-22): no committee, no number - a number
// minted under a committee nobody established is the wrong-value-is-worse-than-absent case (doctrine 5).
export function caseNumberFor(committee, year, seq) {
  const cmte = String(committee || '').trim();
  if (!cmte) {
    throw new Error('No General Committee on this case - a case number cannot be issued without one. '
      + 'Set the railroad and craft so the committee resolves, then save again.');
  }
  return `DISC-${cmte.replace('GO-', 'GO')}-${year}-${String(seq).padStart(3, '0')}`;
}

// counters/discipline_<committee>_<year> - the id the browser used, so the live counter carries on unbroken.
export function counterIdFor(committee, year) {
  return 'discipline_' + String(committee || '').trim() + '_' + year;
}

// The sequence a stored number carries for this committee and year, or 0 when it is not one of theirs.
export function seqOf(caseNumber, committee, year) {
  const cmte = String(committee || '').trim();
  if (!cmte) return 0;
  const prefix = 'DISC-' + cmte.replace('GO-', 'GO') + '-' + year + '-';
  const s = String(caseNumber || '');
  if (s.indexOf(prefix) !== 0) return 0;
  const rest = s.slice(prefix.length);
  if (!/^\d+$/.test(rest)) return 0;
  return parseInt(rest, 10);
}

// The highest sequence already issued - how the counter is initialised on its first use.
export function maxSeq(caseNumbers, committee, year) {
  let max = 0;
  (caseNumbers || []).forEach(function (n) { const q = seqOf(n, committee, year); if (q > max) max = q; });
  return max;
}

// How a note signs the actor's role (the stored vocabulary of the case screen: member | lc | gc | admin).
export function noteRole(role) {
  if (role === 'admin') return 'admin';
  if (role === 'general_chair') return 'gc';
  if (role === 'local_chair') return 'lc';
  return 'member';
}

// ---- THE PLAN ----------------------------------------------------------------------------------------------------
// What an allowed act writes. The function adds updatedBy (the caller's uid), updatedAt and the server time for each
// field in stampFields, appends documentRow (if any) to documents, and writes noteText as a note signed by the actor.
// assignedNumber is the number the function drew from the counter in the same transaction ('' when the case already
// has one).
export function planAct(p) {
  const k = p.kase || {};
  const a = ladderActor(p.actor);
  const kind = p.kind;
  const m = may(a, kind, k);
  if (!m.ok) return { ok: false, because: m.because, why: m.why };
  const who = a.name || a.email || a.uid;
  const patch = {};
  const stampFields = [];
  let documentRow = null;
  let noteText = '';
  const numbered = String(p.assignedNumber || '');
  if (numbered) patch.caseNumber = numbered;
  const numberLine = numbered ? ' Case number assigned: ' + numbered + '.' : '';

  if (kind === 'advance') {
    patch.status = m.to;
    noteText = (String(k.status) === DRAFT
      ? 'Case opened at ' + STAGE_LABELS[m.to] + ' by ' + who + '.'
      : 'Case advanced to: ' + STAGE_LABELS[m.to] + ' by ' + who + '.') + numberLine;
  } else if (kind === 'forward') {
    patch.status = GC_REVIEW;
    patch.forwardedToGCBy = a.email || a.uid;
    patch.forwardedToGCByUid = a.uid;
    stampFields.push('forwardedToGCAt');
    noteText = 'CASE FORWARDED TO GENERAL CHAIRPERSON by ' + who + '. The investigation record has been certified complete. '
      + 'The General Chairperson now has exclusive authority over this case.' + numberLine;
  } else if (kind === 'decide') {
    const v = validateDecision(p.decision, p.caseId);
    if (!v.ok) return { ok: false, because: v.problems.join(' '), why: 'input' };
    patch.decision = v.value.outcome;
    patch.decisionDate = v.value.decisionDate;
    patch.decisionNotes = v.value.notes;
    patch.decisionTerms = v.value.terms;   // T5 / D-15
    patch.decisionLetterPath = v.value.letterPath || String(k.decisionLetterPath || '');
    patch.decidedBy = a.uid;
    patch.decidedByName = who;
    stampFields.push('decidedAt');
    if (v.value.letterPath) {
      documentRow = {
        name: v.value.letterPath.split('/').pop(), url: '', storagePath: v.value.letterPath, type: 'Letter of Decision',
        uploadedBy: a.email || '', uploadedByName: a.name || '', uploadedByRole: noteRole(a.role),
        uploadedAt: String(p.nowIso || ''), visibleTo: ['lc', 'gc', 'admin'], zone: 'decision', isOriginalChargeDocument: false,
      };
    }
    const before = hasDecision(k) ? ' (replacing "' + String(k.decision) + '"' + (k.decisionDate ? ' dated ' + String(k.decisionDate) : '') + ')' : '';
    noteText = 'Decision recorded by ' + who + ': ' + v.value.outcome + ', dated ' + v.value.decisionDate + '.' + before
      + (v.value.letterPath ? ' Letter of decision filed on the case.' : '') + numberLine;
  } else if (kind === 'resolve') {
    patch.status = RESOLVED;
    patch.resolvedBy = a.uid;
    patch.resolvedByName = who;
    stampFields.push('resolvedAt');
    noteText = 'Case resolved by ' + who + '. Outcome on record: ' + String(k.decision) + (k.decisionDate ? ', dated ' + String(k.decisionDate) : '') + '.' + numberLine;
  } else {
    const r = validateReason(p.reason);
    if (!r.ok) return { ok: false, because: r.problem, why: 'input' };
    patch.status = m.to;
    patch.reopenedBy = a.uid;
    patch.reopenedByName = who;
    patch.reopenReason = r.value;
    stampFields.push('reopenedAt');
    noteText = 'Case reopened by ' + who + ', back to ' + STAGE_LABELS[m.to] + '. Reason: ' + r.value + numberLine;
  }
  return {
    ok: true, to: m.to, patch: patch, stampFields: stampFields, documentRow: documentRow, noteText: noteText,
    auditReason: 'disciplineAct ' + kind + (patch.status ? ' -> ' + patch.status : ''),
  };
}
